/**
 * Location Group API Contract
 *
 * Defines the oRPC contract for Location Groups - rule-based or manual location groupings.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateLocationGroupInputSchema, UpdateLocationGroupInputSchema, AddLocationGroupMembersInputSchema, LocationGroupAvailableRuleFieldsResponseSchema, LocationGroupResponseSchema, LocationGroupDetailResponseSchema, ListLocationGroupsResponseSchema, LocationGroupApplyRulesResponseSchema, LocationGroupAddMembersResponseSchema, LocationGroupRemoveMemberResponseSchema, } from "@hourly/api/locationGroup";
import { uuidSchema } from "@hourly/api/validation-common";
// ============ Contract Definitions ============
// Create a location group
const createLocationGroup = oc
    .route({
    method: "POST",
    path: "/location-groups",
    summary: "Create a new location group (SmartGroup or Manual)",
    tags: ["LocationGroups"],
    successStatus: 201,
})
    .input(CreateLocationGroupInputSchema)
    .output(LocationGroupResponseSchema);
// Get a location group by ID
const getLocationGroup = oc
    .route({
    method: "GET",
    path: "/location-groups/{id}",
    summary: "Get a location group by ID",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationGroupResponseSchema);
// Get a location group with members
const getLocationGroupWithMembers = oc
    .route({
    method: "GET",
    path: "/location-groups/{id}/members",
    summary: "Get a location group with its members",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationGroupDetailResponseSchema);
// List location groups for a company
const listLocationGroups = oc
    .route({
    method: "GET",
    path: "/location-groups",
    summary: "List location groups for a company",
    tags: ["LocationGroups"],
})
    .input(z.object({
    limit: z.coerce.number().min(1).max(100).optional().default(100),
    offset: z.coerce.number().min(0).optional().default(0),
}))
    .output(ListLocationGroupsResponseSchema);
// Update a location group
const updateLocationGroup = oc
    .route({
    method: "PUT",
    path: "/location-groups/{id}",
    summary: "Update a location group",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateLocationGroupInputSchema))
    .output(LocationGroupResponseSchema);
// Delete a location group
const deleteLocationGroup = oc
    .route({
    method: "DELETE",
    path: "/location-groups/{id}",
    summary: "Delete a location group",
    tags: ["LocationGroups"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// Apply rules to a location group (re-evaluate and update members)
const applyLocationGroupRules = oc
    .route({
    method: "POST",
    path: "/location-groups/{id}/apply-rules",
    summary: "Apply rules to a location group and update members",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(LocationGroupApplyRulesResponseSchema);
// Get available rule fields for a company
const getAvailableRuleFields = oc
    .route({
    method: "GET",
    path: "/location-groups/rule-fields",
    summary: "Get available fields and values for location group rules",
    tags: ["LocationGroups"],
})
    .input(z.object({}))
    .output(LocationGroupAvailableRuleFieldsResponseSchema);
// ============ Manual Group Member Management ============
// Add members to a manual group
const addGroupMembers = oc
    .route({
    method: "POST",
    path: "/location-groups/{id}/members",
    summary: "Add members to a manual location group",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema }).merge(AddLocationGroupMembersInputSchema))
    .output(LocationGroupAddMembersResponseSchema);
// Remove a member from a manual group
const removeGroupMember = oc
    .route({
    method: "DELETE",
    path: "/location-groups/{id}/members/{locationId}",
    summary: "Remove a member from a manual location group",
    tags: ["LocationGroups"],
})
    .input(z.object({ id: uuidSchema, locationId: uuidSchema }))
    .output(LocationGroupRemoveMemberResponseSchema);
// ============ Export Contract ============
export const locationGroupContract = {
    create: createLocationGroup,
    get: getLocationGroup,
    getWithMembers: getLocationGroupWithMembers,
    list: listLocationGroups,
    update: updateLocationGroup,
    delete: deleteLocationGroup,
    applyRules: applyLocationGroupRules,
    getAvailableRuleFields,
    addMembers: addGroupMembers,
    removeMember: removeGroupMember,
};
//# sourceMappingURL=locationGroup.js.map